import React from 'react';
import { 
  Box, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Chip, 
  Autocomplete, 
  TextField,
  useTheme,
} from '@mui/material';
import ToggleButton from '../common/ToggleButton';

const DemandFilter = ({
  demandFilterType,
  setDemandFilterType,
  selectedUserTypes,
  handleUserTypesChange,
  userCreationPeriod,
  handleCreationPeriodChange,
  selectedUsers,
  handleSpecificUsersChange,
  usersList,
  userTypeOptions,
  creationPeriodOptions,
  onFilterChange,
  demandYAxisPercentage,
  handleDemandYAxisPercentageChange
}) => {
  const theme = useTheme();
  
  const handleFilterTypeChange = (e) => {
    setDemandFilterType(e.target.value);
    // Remove automatic data fetch - only update local state
  };

  return (
    <Box>
      {/* Filter Content */}
      <Box sx={{ 
        p: 3, 
        background: 'transparent'
      }}>
        <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
          <RadioGroup
            row
            value={demandFilterType}
            onChange={handleFilterTypeChange}
            sx={{ 
              mb: 2,
              gap: 3,
              '& .MuiFormControlLabel-root': {
                border: `2px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                px: 2,
                py: 1,
                margin: 0,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.light,
                  backgroundColor: theme.palette.primary.main + '05'
                }
              },
              '& .Mui-checked + .MuiFormControlLabel-label': {
                fontWeight: 600,
                color: theme.palette.primary.main
              }
            }}
          >
            <FormControlLabel 
              value="all" 
              control={<Radio sx={{ color: theme.palette.primary.main }} />} 
              label="All Users" 
            />
            <FormControlLabel 
              value="specific" 
              control={<Radio sx={{ color: theme.palette.primary.main }} />} 
              label="Specific Users" 
            />
          </RadioGroup>
          </FormControl>

        {demandFilterType === 'all' ? (
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontWeight: 500 }}>User Types</InputLabel>
                <Select
                  multiple
                  value={selectedUserTypes}
                  onChange={(e) => handleUserTypesChange(e.target.value)}
                  label="User Types"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: theme.palette.grey[200]
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={userTypeOptions.find(opt => opt.value === value)?.label} 
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.primary.light,
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {userTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontWeight: 500 }}>User Activation Period</InputLabel>
                <Select
                  value={userCreationPeriod || 'all'}
                  onChange={(e) => handleCreationPeriodChange(e.target.value === 'all' ? '' : e.target.value)}
                  label="User Activation Period"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: theme.palette.grey[200]
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  {creationPeriodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={usersList}
                getOptionLabel={(option) => `${option.name} (${option.type})`}
                value={selectedUsers}
                onChange={(event, newValue) => {
                  if (newValue.length <= 3) {
                    handleSpecificUsersChange(newValue);
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={`${option.name}(${option.type})`}
                      {...getTagProps({ index })}
                      key={option.id}
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        fontWeight: 500
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Users (Max 3)"
                    size="medium"
                    helperText={`${selectedUsers.length}/3 users selected`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': {
                          borderWidth: 2,
                          borderColor: theme.palette.grey[200]
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.light
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500
                      }
                    }}
                  />
                )}
                limitTags={3}
                disabled={selectedUsers.length >= 3}
              />
            </Grid>
          </Grid>
        )}
        {/* Y-Axis Percentage Toggle */}
        <Box sx={{ mt: 3 }}>
          <ToggleButton
            checked={demandYAxisPercentage}
            onChange={handleDemandYAxisPercentageChange}
            label="Show as Percentage - out of active users"
            size="medium"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DemandFilter;
